import { existsSync } from "node:fs";
import { mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname, join, resolve } from "node:path";
import {
  claudeGenerator,
  type ComponentIR,
  type GeneratedFile,
} from "@aaac/contracts";
import { parseClaudeConfig, type ClaudeConfig } from "./config.js";

export type FileAction = "created" | "updated" | "unchanged";

export interface SyncOptions {
  projectRoot: string;
  /** Opaque config payload from component.yaml (validated here). */
  config: unknown;
  /** Resolved agent DSL (plain object; the value the runtime embeds). */
  dsl: Record<string, unknown>;
  /** When true, compute changes without writing. */
  dryRun?: boolean;
}

export interface SyncResult {
  files: Array<{ path: string; action: FileAction }>;
}

export interface CheckResult {
  inSync: boolean;
  drift: Array<{ path: string; reason: "missing" | "changed" }>;
}

const CLAUDE_BLOCK_BEGIN = "<!-- aaac:claude-native:begin -->";
const CLAUDE_BLOCK_END = "<!-- aaac:claude-native:end -->";

function buildComponentIR(dsl: Record<string, unknown>, config: ClaudeConfig): ComponentIR {
  const system = dsl.system as { id?: string; version?: string } | undefined;
  const componentId = config.component_id ?? system?.id ?? "component";

  return {
    schema: "aaac/0.1",
    info: {
      id: componentId,
      version: system?.version ?? "1.0.0",
    },
    implementation: config.config_path,
    projections: ["claude"],
    operations: {},
  };
}

function relPath(file: GeneratedFile): string {
  if (file.targetDir && file.targetDir !== ".") {
    return join(file.targetDir, file.path);
  }
  return file.path;
}

function absPath(root: string, file: GeneratedFile): string {
  return join(root, relPath(file));
}

async function readIfExists(path: string): Promise<string | undefined> {
  if (!existsSync(path)) return undefined;
  return readFile(path, "utf8");
}

function renderFiles(dsl: Record<string, unknown>, config: ClaudeConfig): GeneratedFile[] {
  const ir = buildComponentIR(dsl, config);
  return claudeGenerator.generate(ir, { outputDir: "", componentPath: "" });
}

function mergeClaudeMdBlock(
  existing: string | undefined,
  block: string,
): { content: string; action: FileAction } {
  const normalizedBlock = block.trimStart();

  if (existing === undefined) {
    return { content: normalizedBlock, action: "created" };
  }

  const beginIdx = existing.indexOf(CLAUDE_BLOCK_BEGIN);
  const endIdx = existing.indexOf(CLAUDE_BLOCK_END);

  if (beginIdx === -1 || endIdx === -1) {
    const needsSeparator = existing.length > 0 && !existing.endsWith("\n");
    const content = existing + (needsSeparator ? "\n" : "") + normalizedBlock;
    return { content, action: "updated" };
  }

  const before = existing.slice(0, beginIdx);
  let after = existing.slice(endIdx + CLAUDE_BLOCK_END.length);
  // The generated block template ends with a newline; do not treat it as user content.
  if (after === "\n") {
    after = "";
  }
  const content = before + normalizedBlock + after;

  if (content === existing) {
    return { content: existing, action: "unchanged" };
  }
  return { content, action: "updated" };
}

function expectedClaudeMdContent(existing: string | undefined, block: string): string {
  return mergeClaudeMdBlock(existing, block).content;
}

function actionForOverwriteTrue(existing: string | undefined, next: string): FileAction {
  if (existing === undefined) return "created";
  return existing === next ? "unchanged" : "updated";
}

function resolveContent(
  file: GeneratedFile,
  existing: string | undefined,
): { content: string; action: FileAction } {
  if (file.path === "CLAUDE.md" && file.targetDir === "." && !file.overwrite) {
    return mergeClaudeMdBlock(existing, file.content);
  }

  if (!file.overwrite) {
    if (existing !== undefined) {
      return { content: existing, action: "unchanged" };
    }
    if (!file.content) {
      return { content: "", action: "unchanged" };
    }
    return { content: file.content, action: "created" };
  }

  const action = actionForOverwriteTrue(existing, file.content);
  return { content: file.content, action };
}

function checkContent(
  file: GeneratedFile,
  existing: string | undefined,
): "missing" | "changed" | null {
  if (file.path === "CLAUDE.md" && file.targetDir === "." && !file.overwrite) {
    if (existing === undefined) return "missing";
    const expected = expectedClaudeMdContent(existing, file.content);
    return existing === expected ? null : "changed";
  }

  if (!file.overwrite) {
    if (existing === undefined && file.content) return "missing";
    return null;
  }

  if (existing === undefined) return "missing";
  if (existing !== file.content) return "changed";
  return null;
}

/**
 * Materialize `.claude/*` and the CLAUDE.md integration block from the embedded DSL.
 *
 * Idempotent: re-running with no changes writes nothing. CLAUDE.md uses a
 * marker-delimited block merge so user content outside the block is preserved.
 */
export async function syncClaude(opts: SyncOptions): Promise<SyncResult> {
  const config = parseClaudeConfig(opts.config);
  const root = resolve(opts.projectRoot);
  const result: SyncResult = { files: [] };

  for (const file of renderFiles(opts.dsl, config)) {
    const rel = relPath(file);
    const abs = absPath(root, file);
    const existing = await readIfExists(abs);
    const { content, action } = resolveContent(file, existing);

    if (action !== "unchanged" && !opts.dryRun) {
      await mkdir(dirname(abs), { recursive: true });
      await writeFile(abs, content, "utf8");
    }
    result.files.push({ path: rel, action });
  }

  return result;
}

/**
 * Read-only drift gate. Reports files whose on-disk content differs from a
 * fresh render. For CLAUDE.md, drift means the aaac:claude-native block is
 * missing or stale (content outside the block is ignored).
 */
export async function checkClaude(opts: SyncOptions): Promise<CheckResult> {
  const config = parseClaudeConfig(opts.config);
  const root = resolve(opts.projectRoot);
  const drift: CheckResult["drift"] = [];

  for (const file of renderFiles(opts.dsl, config)) {
    const rel = relPath(file);
    const existing = await readIfExists(absPath(root, file));
    const reason = checkContent(file, existing);
    if (reason) {
      drift.push({ path: rel, reason });
    }
  }

  return { inSync: drift.length === 0, drift };
}
